import { useState, type KeyboardEvent } from 'react';
import { T, Var, useGT, useMessages } from 'gt-react';
import {
  CaretDownIcon,
  CaretUpIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { AS400_EXPORT_GROUP_LABELS } from './as400Groups';
import {
  countReviewCells,
  reviewErrorKey,
  stepErrorIndex,
  type ReviewCellError,
} from './as400ReviewErrors';
import { isBlocking } from '@erp/item-master-rules';

/**
 * The review's error summary (GEN-2747): an "N errors" count that opens the
 * list of every cell that breaks a rule, with previous/next buttons that step
 * through them. Picking an entry — clicked, or reached with the arrow keys in
 * the list — jumps the grid to that cell, switching worksheet or page first
 * when it has to. Display only: the errors and the jump come from the owner.
 */
export function ReviewErrorsNavigator({
  errors,
  activeIndex,
  describe,
  onJump,
}: {
  errors: readonly ReviewCellError[];
  /** The error last jumped to, if it is still in the list. */
  activeIndex: number | null;
  /** What is wrong with the error's cell, naming its column. */
  describe: (error: ReviewCellError) => string;
  onJump: (index: number) => void;
}) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  if (errors.length === 0) return null;
  const cells = countReviewCells(errors);
  const step = (direction: 1 | -1) => {
    const next = stepErrorIndex(activeIndex, errors.length, direction);
    if (next !== null) onJump(next);
  };
  return (
    <div className="flex items-center">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              'rounded-r-none',
              cells.errors > 0
                ? 'border-destructive/50 text-destructive'
                : 'border-amber-500/50 text-amber-700 dark:text-amber-400',
            )}
          >
            <WarningCircleIcon className="h-4 w-4" />
            <SummaryCount errors={cells.errors} warnings={cells.warnings} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-96 p-0" align="end">
          <ReviewErrorList
            errors={errors}
            activeIndex={activeIndex}
            describe={describe}
            onJump={onJump}
          />
        </PopoverContent>
      </Popover>
      <Button
        variant="outline"
        size="sm"
        className="rounded-none border-l-0 px-2"
        aria-label={gt('Previous error', {
          context: 'review toolbar button: jump to the previous invalid cell',
        })}
        onClick={() => step(-1)}
      >
        <CaretUpIcon className="h-4 w-4" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="rounded-l-none border-l-0 px-2"
        aria-label={gt('Next error', {
          context: 'review toolbar button: jump to the next invalid cell',
        })}
        onClick={() => step(1)}
      >
        <CaretDownIcon className="h-4 w-4" />
      </Button>
    </div>
  );
}

/**
 * The summary's count: errors (which stop Save) and warnings (which don't),
 * naming only the kinds there are.
 */
function SummaryCount({
  errors,
  warnings,
}: {
  errors: number;
  warnings: number;
}) {
  if (warnings === 0) {
    return errors === 1 ? (
      <T context="review toolbar: one cell breaks a validation rule; opens the list">
        1 error
      </T>
    ) : (
      <T context="review toolbar: how many cells break a validation rule; opens the list">
        <Var>{errors}</Var> errors
      </T>
    );
  }
  if (errors === 0) {
    return warnings === 1 ? (
      <T context="review toolbar: one cell draws a validation warning (saving still allowed); opens the list">
        1 warning
      </T>
    ) : (
      <T context="review toolbar: how many cells draw a validation warning (saving still allowed); opens the list">
        <Var>{warnings}</Var> warnings
      </T>
    );
  }
  return (
    <T context="review toolbar: how many cells break a validation rule, and how many more only draw a warning; opens the list">
      <Var>{errors}</Var> errors, <Var>{warnings}</Var> warnings
    </T>
  );
}

/** Moves focus between the list's entries, as the arrow keys ask. */
function focusSibling(event: KeyboardEvent<HTMLElement>): void {
  const offset = { ArrowDown: 1, ArrowUp: -1 }[event.key];
  if (offset === undefined) return;
  event.preventDefault();
  const entries = [
    ...event.currentTarget.querySelectorAll<HTMLButtonElement>('button'),
  ];
  const { target } = event;
  const at = entries.findIndex(
    (entry) => target instanceof Node && entry.contains(target),
  );
  const next = entries[(at + offset + entries.length) % entries.length];
  next?.focus();
  next?.click();
}

/**
 * The errors, one entry each: what is wrong, then which worksheet, row (item
 * number and CDB key) and page it is on. Arrow keys walk the list and jump as
 * they go, so the grid follows the keyboard.
 */
function ReviewErrorList({
  errors,
  activeIndex,
  describe,
  onJump,
}: {
  errors: readonly ReviewCellError[];
  activeIndex: number | null;
  describe: (error: ReviewCellError) => string;
  onJump: (index: number) => void;
}) {
  const gt = useGT();
  const m = useMessages();
  return (
    <ul
      className="max-h-80 overflow-y-auto py-1"
      aria-label={gt('Cells that need fixing', {
        context: 'accessible name of the review error list',
      })}
      onKeyDown={focusSibling}
    >
      {errors.map((error, index) => (
        <li key={reviewErrorKey(error)}>
          <button
            type="button"
            aria-current={index === activeIndex ? 'true' : undefined}
            className={cn(
              'w-full px-3 py-2 text-left text-xs hover:bg-accent focus-visible:bg-accent focus-visible:outline-none',
              index === activeIndex && 'bg-accent',
            )}
            onClick={() => onJump(index)}
          >
            <span
              className={cn(
                'block',
                !isBlocking(error.violation) &&
                  'text-amber-700 dark:text-amber-400',
              )}
            >
              {isBlocking(error.violation) ? null : (
                <T context="review error list: prefix on an entry that is only a warning (saving is still allowed)">
                  Warning:{' '}
                </T>
              )}
              {describe(error)}
            </span>
            <span className="mt-0.5 block text-muted-foreground">
              <T context="review error list: where an invalid cell is — worksheet name, item number, CDB (company/division/branch) key, and page">
                <Var>{m(AS400_EXPORT_GROUP_LABELS[error.group])}</Var> ·{' '}
                <Var>{error.itemNumber ?? '—'}</Var> ·{' '}
                <Var>{error.cdb ?? '—'}</Var> · page <Var>{error.page}</Var>
              </T>
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
